import api from './api';
import {
  SchedulingLeg,
  LegScheduleUpdate,
  SendToLCRequest,
  ReturnToSchedulerRequest,
  RunningTotalsResponse,
  FilterOptionsResponse,
  SchedulerFilters,
  SchedulingHistoryRecord,
  DriverAvailabilityResponse,
  TerminalSettings,
  TerminalSettingsUpdate,
  RescheduleHistoryItem,
  LcResolutionItem,
} from '../types/scheduling';

class SchedulingService {
  private baseUrl = '/scheduling';

  async getLegs(filters: SchedulerFilters, additionalFields?: string[]): Promise<SchedulingLeg[]> {
    const params: Record<string, string | number | undefined> = {
      move_type: filters.move_type,
      include_prepull_needed: filters.include_prepull_needed ? 'true' : undefined,
      terminal: filters.terminal?.join(','),
      delivery_type: filters.delivery_type,
      status: filters.status?.join(','),
      customer: filters.customer?.join(','),
      logistic_coordinator: filters.logistic_coordinator?.join(','),
      pickup_from: filters.pickup_from,
      deliver_to: filters.deliver_to,
      restriction: filters.restriction,
      prepull_filter: filters.prepull_filter,
      mileage_band: filters.mileage_band,
      appt_date: filters.appt_date,
      pronumber: filters.pronumber,
      include_null_status: filters.include_null_status !== undefined ? String(filters.include_null_status) : undefined,
    };

    // Remove undefined values
    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === '') {
        delete params[key];
      }
    });

    // Unsorted: the backend keeps the user's selection order. Added after the
    // undefined-stripping above so an empty selection sends no param at all.
    if (additionalFields?.length) params.additional_fields = additionalFields.join(',');

    const response = await api.get<SchedulingLeg[]>(`${this.baseUrl}/legs`, { params });
    return response.data;
  }

  async getReschedulesForOrder(pronumber: number): Promise<RescheduleHistoryItem[]> {
    const response = await api.get<RescheduleHistoryItem[]>(
      `${this.baseUrl}/orders/${pronumber}/reschedules`
    );
    return response.data;
  }

  async updateReschedule(
    recno: number,
    patch: {
      servicefailuredate?: string | null;
      reason?: string | null;
      servicefailuretype?: string | null;
      thd_reason?: string | null;
      responsibledriver?: string | null;
      comments?: string | null;
      /** Reviewed checkbox — flag only; the backend owns the reviewer/time stamp. */
      reviewed?: 'Y' | 'N';
    },
  ): Promise<RescheduleHistoryItem> {
    const response = await api.patch<RescheduleHistoryItem>(
      `${this.baseUrl}/reschedules/${recno}`,
      patch,
    );
    return response.data;
  }

  async createReschedule(
    pronumber: number,
    body: {
      olddate?: string | null;
      newdate?: string | null;
      servicefailuredate?: string | null;
      reason?: string;
      servicefailuretype?: string;
      thd_reason?: string;
      responsibledriver?: string;
      comments?: string;
      /** When true, a unique-key collision returns the existing row instead
       *  of a 409 (idempotent appt-date-change reschedule path). */
      allow_existing?: boolean;
    },
  ): Promise<RescheduleHistoryItem> {
    const response = await api.post<RescheduleHistoryItem>(
      `${this.baseUrl}/orders/${pronumber}/reschedules`,
      body,
    );
    return response.data;
  }

  async listRescheduleReasons(signal?: AbortSignal): Promise<string[]> {
    const response = await api.get<string[]>(
      `${this.baseUrl}/reschedule-reasons`,
      { signal },
    );
    return response.data;
  }

  /** The single reschedule (service failure) linked to a trouble ticket, or null. */
  async getRescheduleByTicket(
    ticketId: number,
    signal?: AbortSignal,
  ): Promise<RescheduleHistoryItem | null> {
    const response = await api.get<RescheduleHistoryItem | null>(
      `${this.baseUrl}/reschedules/by-ticket/${ticketId}`,
      { signal },
    );
    return response.data;
  }

  /** Create-or-update the reschedule tied to a trouble ticket (1:1). */
  async upsertRescheduleForTicket(
    ticketId: number,
    body: {
      servicefailuredate?: string | null;
      reason?: string;
      servicefailuretype?: string;
      thd_reason?: string;
      responsibledriver?: string;
      comments?: string;
    },
  ): Promise<RescheduleHistoryItem> {
    const response = await api.put<RescheduleHistoryItem>(
      `${this.baseUrl}/reschedules/by-ticket/${ticketId}`,
      body,
    );
    return response.data;
  }

  async getLcResolution(
    csrs?: string[],
    signal?: AbortSignal,
    additionalFields?: string[],
  ): Promise<LcResolutionItem[]> {
    const params: Record<string, string> = {};
    if (csrs && csrs.length > 0) params.csr = csrs.join(',');
    // Unsorted: the backend keeps the user's selection order.
    if (additionalFields && additionalFields.length > 0) {
      params.additional_fields = additionalFields.join(',');
    }
    const response = await api.get<LcResolutionItem[]>(
      `${this.baseUrl}/lc-resolution`,
      { params, signal },
    );
    return response.data;
  }

  /**
   * Report variant of the TTs & Service Failures queue: all tickets + service
   * failures within a date range, scoped by LC + region, filtered to the given
   * ticket statuses (defaults to RESOLVED server-side when omitted).
   */
  async getLcResolutionReport(
    args: {
      startDate: string;
      endDate: string;
      csrs?: string[];
      regions?: string[];
      statuses?: string[];
      dateBasis?: string;
      additionalFields?: string[];
    },
    signal?: AbortSignal,
  ): Promise<LcResolutionItem[]> {
    const params: Record<string, string> = {
      start_date: args.startDate,
      end_date: args.endDate,
    };
    if (args.csrs && args.csrs.length > 0) params.csr = args.csrs.join(',');
    if (args.regions && args.regions.length > 0) params.region = args.regions.join(',');
    if (args.statuses && args.statuses.length > 0) params.status = args.statuses.join(',');
    if (args.dateBasis) params.date_basis = args.dateBasis;
    if (args.additionalFields && args.additionalFields.length > 0) {
      params.additional_fields = args.additionalFields.join(',');
    }
    const response = await api.get<LcResolutionItem[]>(
      `${this.baseUrl}/lc-resolution-report`,
      { params, signal },
    );
    return response.data;
  }

  async updateLegSchedule(
    pronumber: number,
    update: LegScheduleUpdate
  ): Promise<{ message: string; id: number }> {
    const response = await api.patch(
      `${this.baseUrl}/legs/${pronumber}`,
      update
    );
    return response.data;
  }

  async sendToLC(request: SendToLCRequest): Promise<{ message: string; count: number }> {
    const response = await api.post(`${this.baseUrl}/legs/send-to-lc`, request);
    return response.data;
  }

  async finalizeLegs(request: SendToLCRequest & { prepull_only?: boolean }): Promise<{ message: string; count: number; errors?: string[] }> {
    const response = await api.post(`${this.baseUrl}/legs/finalize`, request);
    return response.data;
  }

  async finalizePrepull(request: SendToLCRequest): Promise<{ message: string; count: number; errors?: string[] }> {
    const response = await api.post(`${this.baseUrl}/legs/finalize-prepull`, request);
    return response.data;
  }

  async returnToScheduler(
    request: ReturnToSchedulerRequest
  ): Promise<{ message: string; count: number; errors?: string[] }> {
    const response = await api.post(`${this.baseUrl}/legs/return-to-scheduler`, request);
    return response.data;
  }

  async setToHold(request: SendToLCRequest): Promise<{ message: string; count: number }> {
    const response = await api.post(`${this.baseUrl}/legs/set-to-hold`, request);
    return response.data;
  }

  async setToPendingCustomer(request: SendToLCRequest): Promise<{ message: string; count: number }> {
    const response = await api.post(`${this.baseUrl}/legs/set-to-pending-customer`, request);
    return response.data;
  }

  async getRunningTotals(terminal?: string[]): Promise<RunningTotalsResponse> {
    const params = terminal?.length ? { terminal: terminal.join(',') } : {};
    const response = await api.get<RunningTotalsResponse>(
      `${this.baseUrl}/running-totals`,
      { params }
    );
    return response.data;
  }

  async getFilterOptions(): Promise<FilterOptionsResponse> {
    const response = await api.get<FilterOptionsResponse>(
      `${this.baseUrl}/filter-options`
    );
    return response.data;
  }

  async getOrderLegs(pronumber: number): Promise<SchedulingLeg[]> {
    const response = await api.get<SchedulingLeg[]>(
      `${this.baseUrl}/orders/${pronumber}/legs`
    );
    return response.data;
  }

  async getHistory(pronumber: number): Promise<SchedulingHistoryRecord[]> {
    const response = await api.get<SchedulingHistoryRecord[]>(
      `${this.baseUrl}/history/${pronumber}`
    );
    return response.data;
  }

  async searchHistory(params: {
    changed_by?: string;
    pronumber?: number;
    start?: string;
    end?: string;
    limit?: number;
  }): Promise<SchedulingHistoryRecord[]> {
    const response = await api.get<SchedulingHistoryRecord[]>(
      `${this.baseUrl}/history`,
      { params }
    );
    return response.data;
  }

  async getHistoryUsers(): Promise<string[]> {
    const response = await api.get<string[]>(`${this.baseUrl}/history/users`);
    return response.data;
  }

  async getDriverAvailability(terminal?: string[]): Promise<DriverAvailabilityResponse> {
    const params = terminal?.length ? { terminal: terminal.join(',') } : {};
    const response = await api.get<DriverAvailabilityResponse>(
      `${this.baseUrl}/driver-availability`,
      { params }
    );
    return response.data;
  }

  async getTerminalSettings(): Promise<TerminalSettings[]> {
    const response = await api.get<TerminalSettings[]>(
      `${this.baseUrl}/terminal-settings`
    );
    return response.data;
  }

  async updateTerminalSettings(
    terminalId: number,
    update: TerminalSettingsUpdate
  ): Promise<TerminalSettings> {
    const response = await api.patch<TerminalSettings>(
      `${this.baseUrl}/terminal-settings/${terminalId}`,
      update
    );
    return response.data;
  }
}

const schedulingService = new SchedulingService();
export default schedulingService;
